/**
 * A sessão e o acesso fiscal — as duas perguntas, separadas.
 *
 * Fonte normativa: SCRIPT G7, §53 (auth ≠ autorização), §59 (primeiro acesso) e
 * §62 (o texto exato de quem entra sem vínculo).
 *
 * AS DUAS PERGUNTAS
 *
 *     "quem é você?"        Supabase Auth responde. auth.users.
 *     "o que você pode?"    account_fiscal_identity_access responde.
 *
 *   O §57 é literal: _"Existir em auth.users não concede acesso fiscal."_ Por
 *   isso este módulo tem dois estados de carregamento e não um. Um usuário
 *   autenticado sem vínculo não é um erro nem uma sessão inválida — é uma
 *   situação prevista, com tela própria.
 *
 *   Fundir as duas perguntas num booleano `isLoggedIn` seria o começo de tratar
 *   sessão como permissão, que é como a autorização migra da RLS para a UI.
 *
 * A AUTORIZAÇÃO NÃO É DECIDIDA AQUI
 *
 *   Este módulo não decide nada: ele PERGUNTA. A lista de identidades vem de um
 *   `select` em `fiscal_identities`, e quem filtra é a política
 *   `fiscal_identities_select_authorized`, no banco.
 *
 *   Se a RLS estiver certa, um usuário sem vínculo recebe zero linhas — não
 *   porque a tela escondeu, mas porque o banco não mandou. É a diferença entre
 *   um console e uma fachada.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { getSupabase } from './supabase.js';

export interface FiscalIdentity {
  readonly id: string;
  readonly tax_id: string;
  readonly legal_name: string;
  readonly trade_name: string | null;
  readonly tax_regime: string;
  readonly status: string;
}

/**
 * Os estados possíveis, como lista fechada.
 *
 * `unauthorized` existe separado de `error` de propósito: um é resposta, o
 * outro é falta de resposta. O §110 exige que falha de consulta não pareça
 * lista vazia, e a primeira forma de garantir isso é não deixar os dois caírem
 * no mesmo nome.
 */
export type AccessState =
  | 'loading'
  | 'anonymous'
  | 'authenticated_without_access'
  | 'authorized'
  | 'error';

export interface SessionContextValue {
  readonly session: Session | null;
  readonly identities: readonly FiscalIdentity[];
  readonly selected: FiscalIdentity | null;
  readonly state: AccessState;
  readonly error: string | null;
  select(identityId: string): void;
  signIn(email: string, password: string): Promise<{ error: string | null }>;
  signOut(): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/** A identidade escolhida sobrevive ao refresh; a sessão é do Supabase. */
const CHAVE_IDENTIDADE = 'optefiscal.identity';

export function SessionProvider({ children }: { children: ReactNode }) {
  const supabase = getSupabase();

  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [identities, setIdentities] = useState<readonly FiscalIdentity[]>([]);
  const [identitiesLoaded, setIdentitiesLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(CHAVE_IDENTIDADE);
    } catch {
      return null;
    }
  });

  /*
   * `getSession` primeiro, `onAuthStateChange` depois.
   *
   * O primeiro restaura a sessão do `localStorage` num refresh (§125); o
   * segundo acompanha login, logout e renovação de token. Só o segundo deixaria
   * a primeira pintura sem sessão e mandaria o usuário para /login por um
   * instante — um "logout" que ninguém pediu.
   */
  useEffect(() => {
    let vivo = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!vivo) return;
        setSession(data.session);
        setSessionLoaded(true);
      })
      .catch(() => {
        if (!vivo) return;
        setError('Não foi possível restaurar a sessão.');
        setSessionLoaded(true);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_evento, novaSessao) => {
      setSession(novaSessao);
      setSessionLoaded(true);
    });

    return () => {
      vivo = false;
      sub.subscription.unsubscribe();
    };
  }, [supabase]);

  /*
   * As identidades são recarregadas quando a sessão muda — inclusive ao sair.
   *
   * Sem isso, a lista de um usuário continuaria em memória depois do logout e o
   * próximo login herdaria dados que não são dele.
   */
  useEffect(() => {
    let vivo = true;

    if (session === null) {
      setIdentities([]);
      setIdentitiesLoaded(sessionLoaded);
      return () => {
        vivo = false;
      };
    }

    setIdentitiesLoaded(false);
    setError(null);

    supabase
      .from('fiscal_identities')
      .select('id, tax_id, legal_name, trade_name, tax_regime, status')
      .order('legal_name')
      .then(({ data, error: erro }) => {
        if (!vivo) return;
        if (erro) {
          /*
           * Erro de consulta NÃO vira lista vazia (§110).
           *
           * Uma falha de rede que aparecesse como "nenhuma identidade" diria ao
           * contribuinte que ele não tem acesso — uma afirmação sobre permissão
           * feita a partir de uma falha de transporte.
           */
          setError(erro.message);
          setIdentities([]);
        } else {
          setIdentities((data ?? []) as FiscalIdentity[]);
        }
        setIdentitiesLoaded(true);
      });

    return () => {
      vivo = false;
    };
  }, [supabase, session, sessionLoaded]);

  const select = useCallback((identityId: string) => {
    setSelectedId(identityId);
    try {
      localStorage.setItem(CHAVE_IDENTIDADE, identityId);
    } catch {
      /* Sessão privada, armazenamento bloqueado. A escolha vale para esta aba. */
    }
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { error: erro } = await supabase.auth.signInWithPassword({ email, password });
      /*
       * A mensagem do provedor é repassada como veio.
       *
       * A documentação oficial avisa que ela não distingue "conta não existe" de
       * "senha errada", e isso é proposital do lado deles. Reescrever para algo
       * mais específico seria inventar informação que ninguém tem.
       */
      return { error: erro ? erro.message : null };
    },
    [supabase],
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    try {
      localStorage.removeItem(CHAVE_IDENTIDADE);
    } catch {
      /* idem */
    }
    setSelectedId(null);
  }, [supabase]);

  const selected = useMemo(() => {
    if (identities.length === 0) return null;
    return identities.find((i) => i.id === selectedId) ?? identities[0] ?? null;
  }, [identities, selectedId]);

  const state: AccessState = useMemo(() => {
    if (!sessionLoaded) return 'loading';
    if (session === null) return 'anonymous';
    if (error !== null) return 'error';
    if (!identitiesLoaded) return 'loading';
    if (identities.length === 0) return 'authenticated_without_access';
    return 'authorized';
  }, [sessionLoaded, session, error, identitiesLoaded, identities]);

  const value = useMemo<SessionContextValue>(
    () => ({ session, identities, selected, state, error, select, signIn, signOut }),
    [session, identities, selected, state, error, select, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (ctx === null) {
    throw new Error('useSession precisa estar dentro de <SessionProvider>.');
  }
  return ctx;
}
